import { flushSync } from "react-dom";

/* ═══════════════════════════════════════════════════════════════
   reportExport — print bus + download helpers + settings/signoff
   normalization for the Phase 10 report.
   ═══════════════════════════════════════════════════════════════ */

export const REPORT_SIGNOFF_STORAGE_KEY = "zentis:reportSignoff:";
export const REPORT_SETTINGS_STORAGE_KEY = "zentis:reportSettings:";

export const DEFAULT_DOCUMENT_SETTINGS = {
  theme: "minimal", // modern | classic | minimal
  includeCover: true,
  includeToc: true,
  includeCharts: true,
  includeAppendix: true,
  showWatermark: true,
  showConfidential: true,
  pageSize: "A4", // A4 | Letter
  orientation: "portrait", // portrait | landscape
  margins: { top: 14, bottom: 14, left: 12, right: 12 },
};

/* ── document settings: UI (camelCase) ↔ backend (snake_case) ── */
export function settingsToBackend(settings = {}) {
  const s = { ...DEFAULT_DOCUMENT_SETTINGS, ...settings };
  return {
    page_size: s.pageSize,
    orientation: s.orientation,
    margins: s.margins,
    theme: s.theme,
    include_cover: s.includeCover,
    include_toc: s.includeToc,
    include_charts: s.includeCharts,
    include_appendix: s.includeAppendix,
    watermark: s.showWatermark ? "CONFIDENTIAL" : "",
  };
}

export function settingsFromBackend(raw = {}) {
  return {
    ...DEFAULT_DOCUMENT_SETTINGS,
    theme: raw.theme || "minimal",
    includeCover: raw.include_cover !== false,
    includeToc: raw.include_toc !== false,
    includeCharts: raw.include_charts !== false,
    includeAppendix: raw.include_appendix !== false,
    showWatermark: raw.watermark !== "",
    showConfidential: raw.include_cover === undefined ? true : raw.include_cover !== false,
    pageSize: raw.page_size || "A4",
    orientation: raw.orientation || "portrait",
    margins: raw.margins || DEFAULT_DOCUMENT_SETTINGS.margins,
  };
}

export function signoffToBackend(signoff = {}) {
  return {
    reviewer: signoff.reviewer || "",
    comment: signoff.comment || "",
    confirmed: !!signoff.confirmed,
  };
}

/* ── filenames ─────────────────────────────────────────────── */
export function sanitizeFilename(s) {
  return String(s || "Audit_Report").replace(/[\s/\\]+/g, "_").replace(/[^A-Za-z0-9_\-.]/g, "");
}

export function reportFilename(auditType, ext, date = new Date()) {
  const iso = date.toISOString().split("T")[0];
  return `${sanitizeFilename(auditType)}_Audit_Report_${iso}.${ext}`;
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/* ── print page geometry (@page) derived from document settings ──
   Injected as a <style> right before window.print() so @page size and
   margins follow the Document Settings panel. Removed after printing. */
export function printPageCss(settings = {}) {
  const size = String(settings.pageSize || "A4").toLowerCase() === "letter" ? "letter" : "a4";
  const orient = settings.orientation === "landscape" ? "landscape" : "portrait";
  const m = settings.margins || {};
  return `@page { size: ${size} ${orient}; margin: ${m.top ?? 14}mm ${m.right ?? 12}mm ${m.bottom ?? 14}mm ${m.left ?? 12}mm; }`;
}

/* Printable content width (CSS px) for a given settings object. Matches the
   @page geometry from applyPrintPageCss, so charts measured off-screen keep
   the exact layout when the print snapshot re-lays out at the same width. */
const MM_TO_PX = 96 / 25.4;

export function printWidthPx(settings = {}) {
  const isLetter = String(settings.pageSize || "A4").toLowerCase() === "letter";
  const landscape = settings.orientation === "landscape";
  const pageW = landscape ? (isLetter ? 279.4 : 297) : isLetter ? 215.9 : 210;
  const m = settings.margins || {};
  const hMargin = (m.left ?? 12) + (m.right ?? 12);
  return Math.round((pageW - hMargin) * MM_TO_PX);
}

const PRINT_PAGE_STYLE_ID = "rd-print-page-css";

export function applyPrintPageCss(settings = {}) {
  try {
    let style = document.getElementById(PRINT_PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = PRINT_PAGE_STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = printPageCss(settings);
  } catch {
    // ignore — the static ReportStyles.css @page rule is the fallback
  }
}

export function removePrintPageCss() {
  try {
    const style = document.getElementById(PRINT_PAGE_STYLE_ID);
    if (style) style.remove();
  } catch {
    // ignore
  }
}

/* ── print bus ───────────────────────────────────────────────
   Phase10Report writes a job; the dedicated #report-print-root bridge
   (ReportPrintDocument) renders <ReportDocument printMode> and the browser
   prints ONLY that root (see ReportStyles.css @media print). */
let pendingJob = null;
const listeners = new Set();

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) fn(pendingJob);
}

export function getPendingPrintJob() {
  return pendingJob;
}

export function subscribePrintJob(fn) {
  return subscribe(fn);
}

/* Guards against stacking window.print() dialogs. Chrome blocks the tab while
   the dialog is open, so a re-entrant call (double-click on Export) is ignored. */
let printInFlight = false;

/* Mounts the pending job into the print root, waits for the bridge to paint,
   then triggers window.print().

   The root is display:none on screen, so before the snapshot we briefly show it
   off-screen at the exact printable width — otherwise Recharts
   ResponsiveContainer measures 0×0 inside the hidden root and the charts print
   blank. The off-screen position is cleared right before window.print() so the
   @media print CSS (ReportStyles.css) re-displays the root in-flow for the
   snapshot. */
export function requestPrint(job, { firstTime = false, toast = null } = {}) {
  if (printInFlight) return;
  printInFlight = true;
  pendingJob = job;
  notify();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const root = document.getElementById("report-print-root");
      // Mount hidden → force-layout off-screen at the exact printable width so
      // Recharts ResponsiveContainer measures real dimensions (inside a
      // display:none parent they measure 0×0 and would print blank).
      if (root) {
        root.style.cssText = `display:block !important; position:fixed !important; left:-9999px !important; top:0 !important; width:${printWidthPx(job?.settings)}px !important;`;
      }
      try {
        flushSync(() => notify());
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Charts have settled at real size — restore the hidden state so
            // the @media print CSS re-displays the root in-flow for the
            // snapshot (position:fixed would otherwise push it off-page).
            if (root) root.style.cssText = "";
            applyPrintPageCss(job?.settings);
            // window.print() blocks in Chrome/Firefox but returns immediately in
            // Safari — so the print root and @page style stay mounted until
            // `afterprint` actually fires. A long safety-net timeout only guards
            // browsers that never fire `afterprint` (e.g. print cancelled).
            let settled = false;
            const cleanup = () => {
              if (settled) return;
              settled = true;
              printInFlight = false;
              window.removeEventListener("afterprint", onAfterPrint);
              clearTimeout(fallback);
              removePrintPageCss();
              pendingJob = null;
              notify();
            };
            const onAfterPrint = () => cleanup();
            const fallback = setTimeout(cleanup, 5 * 60 * 1000);
            window.addEventListener("afterprint", onAfterPrint);
            window.print();
          });
        });
      } catch (err) {
        // Never leave the print root wedged in the off-screen measure state.
        if (root) root.style.cssText = "";
        printInFlight = false;
        pendingJob = null;
        notify();
        throw err;
      }
    });
  });
  if (firstTime && toast) {
    toast("PDF export: in the print dialog choose “Save as PDF” to download a crisp, searchable copy.", {
      description:
        "Tip: in the print dialog open “More settings” and switch OFF “Headers and footers” to remove the page URL from the PDF footer.",
    });
  }
}

/* ── HTML export ────────────────────────────────────────────────
   Renders the same ReportDocument the print flow uses (so charts get real
   layout width) inside #report-print-root, then serializes its markup into a
   standalone, self-contained HTML file.
*/
export function exportHtml(job, { toast = null } = {}) {
  if (printInFlight) return;
  printInFlight = true;
  pendingJob = job;
  notify();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const root = document.getElementById("report-print-root");
      const doc = root?.querySelector(".report-document");
      if (!root || !doc) {
        pendingJob = null;
        notify();
        printInFlight = false;
        toast?.(`HTML export failed: print root not mounted.`);
        return;
      }
      const previous = root.style.cssText;
      root.style.cssText =
        "display:block !important; position:fixed !important; left:-9999px !important; top:0 !important; width:860px !important; pointer-events:none !important; z-index:-1 !important;";
      requestAnimationFrame(() => {
        try {
          const html = buildHtmlPage(job, doc.outerHTML);
          downloadBlob(new Blob([html], { type: "text/html" }), reportFilename(job?.derived?.auditType || "Audit", "html"));
          toast?.("HTML exported");
        } finally {
          root.style.cssText = previous;
          pendingJob = null;
          notify();
          printInFlight = false;
        }
      });
    });
  });
}

function buildHtmlPage(job, bodyHtml) {
  const d = job?.derived || {};
  const auditType = String(d.auditType || "Audit");
  const orgName = String(d.orgName || "Organization");
  const title = `${auditType} Audit Report`;
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${title}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, Arial, sans-serif; max-width: 860px; margin: 0 auto; padding: 32px 24px 64px; color: #1a1a1a; line-height: 1.6; }
  table { width: 100%; border-collapse: collapse; margin: 1em 0; }
  th, td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; font-size: 13px; vertical-align: top; }
  th { background: #f9fafb; font-weight: 600; }
  .report-document { background: #ffffff; }
  .report-document * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .rd-no-break, .report-document table, .report-document tr { break-inside: avoid; page-break-inside: avoid; }
  @media print { .report-document table, .report-document tr { break-inside: avoid; } }
</style>
</head>
<body>
${bodyHtml}
<footer style="margin-top:40px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:11px;color:#6b7280">
  ${auditType} Audit Report — ${orgName} — Exported ${new Date().toLocaleString()}
</footer>
</body>
</html>`;
}